// Monthly time-block view — 24-hour grouped by time segment for one month
// Covers full 0-23 hours including 凌晨 (0-5) and 夜间 (22-23) — typically sleep periods

import React from 'react';
import { CATEGORIES, CAT_BY_ID, CAT_TREE } from '../data/categories.js';
import { SLOTS, parseDate, dateKey } from '../data/slots.js';
import { QuickApplyShuttleModal } from './dailyTable.jsx';

// N43 · 半小时精度：slot 边界工具（整点格也是 slot，行为兼容）
const SLOT_STARTS = SLOTS.map(s => s.h * 60 + s.m);
const SLOT_ENDS = SLOTS.map(s => s.h * 60 + s.m + (s.span === 0.5 ? 30 : 60));
const fmtMin = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`; // 1440 → "24:00"

const MOBILE_SEGMENTS = [
  { label: '凌晨', range: [0, 5],   tone: 'night' },
  { label: '早晨', range: [6, 9],   tone: 'day'   },
  { label: '上午', range: [10, 12], tone: 'day'   },
  { label: '中午', range: [13, 15], tone: 'day'   },
  { label: '下午', range: [16, 18], tone: 'day'   },
  { label: '晚上', range: [19, 21], tone: 'day'   },
  { label: '夜间', range: [22, 23], tone: 'night' },
];

function MonthlyView({ data, year = 2026, month = 4, openBlock, onOpenBlock, customCats, catOrder, onChange }) {
  // 移动端：点标题「2026年4月」弹年/月选择器（替代年/月 chip 栏）。只列有数据的月
  const [showYMPicker, setShowYMPicker] = React.useState(false);
  const { years: ymYears, monthsFor: ymMonthsFor } = buildYearMonths(data, year, month);
  // 说明文字（PC 放标题下；移动端移到图例下面，不钉在表头省垂直空间，也避免滚动时闪）
  const SUB_TEXT = '每个方块代表一小时 · 凌晨 0-5 / 夜间 22-23 默认按睡眠呈现，有数据时按真实记录展示';
  // 按分类目录树 DFS + catOrder 拖拽顺序展平所有 cat（跟侧栏 / 穿梭框一致）
  const orderedCats = React.useMemo(() => {
    const childMap = {};
    CAT_TREE.forEach(n => { if (n.children) childMap[n.id] = [...n.children]; });
    (customCats || []).forEach(c => {
      if (!childMap[c.parentId]) childMap[c.parentId] = [];
      childMap[c.parentId].push(c.id);
    });
    if (catOrder) {
      Object.keys(childMap).forEach(pid => {
        if (catOrder[pid]) {
          const ord = catOrder[pid];
          childMap[pid].sort((a, b) =>
            (ord.indexOf(a) === -1 ? 999 : ord.indexOf(a)) -
            (ord.indexOf(b) === -1 ? 999 : ord.indexOf(b))
          );
        }
      });
    }
    const builtTop = CAT_TREE.map(n => n.id);
    let topOrder;
    if (catOrder && catOrder.root) {
      topOrder = catOrder.root.filter(id => builtTop.includes(id));
      builtTop.forEach(id => { if (!topOrder.includes(id)) topOrder.push(id); });
    } else {
      topOrder = builtTop;
    }
    const out = [];
    function walk(id) {
      const c = CAT_BY_ID[id];
      if (c) out.push(c);
      (childMap[id] || []).forEach(walk);
    }
    topOrder.forEach(walk);
    return out;
  }, [customCats, catOrder]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const setOpenBlock = onOpenBlock || (() => {});
  // Frosted-glass opacity (0-100), persisted to localStorage. Defaults to 55.
  const [frostAlpha, setFrostAlpha] = React.useState(() => {
    try {
      const v = localStorage.getItem('monthlyFrostAlpha');
      return v != null ? Math.min(100, Math.max(0, +v)) : 55;
    } catch (e) { return 55; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('monthlyFrostAlpha', String(frostAlpha)); } catch (e) {}
  }, [frostAlpha]);
  // Toggle inline slider by clicking the icon. No popup, no click-outside dismiss —
  // user explicitly toggles by clicking the icon again.
  const [frostOpen, setFrostOpen] = React.useState(false);

  // Date helpers (cross-day walk)
  function nextDateKey(k) {
    const d = parseDate(k); d.setDate(d.getDate() + 1); return dateKey(d);
  }
  function prevDateKey(k) {
    const d = parseDate(k); d.setDate(d.getDate() - 1); return dateKey(d);
  }
  // Find which track at (date, hour) carries the given cat
  function findTrackForCat(date, hour, cat) {
    const day = data[date];
    if (!day || !day.tracks) return 0;
    const slotsAtHour = [];
    for (let i = 0; i < SLOTS.length; i++) if (SLOTS[i].h === hour) slotsAtHour.push(i);
    for (let t = 0; t <= 2; t++) {
      for (const i of slotsAtHour) {
        if (day.tracks[t][i] === cat) return t;
      }
    }
    return 0;
  }

  // For each day, compute dominant category per hour
  // Returns { real, display, halves } —
  //   real[h]: 真实数据派生的 dominant cat（null = 该小时确实没记录）
  //   display[h]: real[h] || 0-5/22-23 的占位 'sleep' fallback
  //   halves[h]: 半小时格小时（HALF_HOUR_HOURS：6–17 / 22 / 23）里前/后半支配分类不同 → [前半cat, 后半cat]（可含 null=空），
  //              否则 null。渲染成对角线双色格（N40：半小时吃饭在月度可见），整格尺寸不变。
  // 渲染时用 real 判断 isReal（决定是否画满高度色块），用 display 决定底纹颜色。
  function dayCells(dateKey) {
    const day = data[dateKey];
    const real = new Array(24).fill(null);
    const display = new Array(24).fill(null);
    const halves = new Array(24).fill(null);
    const isNightHour = (h) => h < 6 || h >= 22;
    if (!day) {
      // 完全没数据的天 — 只有"夜间小时"显示占位 sleep
      for (let h = 0; h < 24; h++) display[h] = isNightHour(h) ? 'sleep' : null;
      return { real, display, halves };
    }
    for (let h = 0; h < 24; h++) {
      const idxs = SLOTS.map((s, i) => ({ s, i })).filter(o => o.s.h === h).map(o => o.i);
      const counts = {};
      if (idxs.length === 0) {
        // SLOTS 全覆盖 0-23，这里实际不会进；保留 fallback 也只算 display 占位
        display[h] = isNightHour(h) ? 'sleep' : null;
        continue;
      }
      idxs.forEach(i => {
        for (let t = 0; t <= 2; t++) {
          const c = day.tracks[t][i];
          if (c) counts[c] = (counts[c] || 0) + (t === 1 ? 2 : 1);
        }
      });
      let best = null, bestN = 0;
      for (const k in counts) if (counts[k] > bestN) { best = k; bestN = counts[k]; }
      real[h] = best;
      // If we found nothing AND hour is early-morning/late, fallback to placeholder sleep
      display[h] = best || (isNightHour(h) ? 'sleep' : null);
      // 半小时格小时：前/后半各自取支配分类（睡眠轨同样 ×2 权重），不同就切对角线
      if (idxs.length === 2) {
        const domAt = (i) => {
          let b = null, bn = 0;
          for (let t = 0; t <= 2; t++) {
            const c = day.tracks[t][i];
            if (!c) continue;
            const n = t === 1 ? 2 : 1;
            if (n > bn) { b = c; bn = n; }
          }
          return b;
        };
        const first = domAt(idxs[0]), second = domAt(idxs[1]);
        if ((first || second) && first !== second) halves[h] = [first, second];
      }
    }
    return { real, display, halves };
  }

  // N42：双色格点击 → 预先把前/后半两个分类各自的块都找好，面板顶部 tab 切换加载哪个。
  // split = { date, hour, cats: [前半cat, 后半cat], blocks: { cat: block } }（block 互相带同一个 split）
  function buildSplit(date, hour, halves) {
    const split = { date, hour, cats: [halves[0], halves[1]], blocks: {} };
    halves.forEach(c => {
      const b = findBlock(date, hour, c);
      b.split = split;
      split.blocks[c] = b;
    });
    return split;
  }

  // Real cat at (date, hour) — bypass dayCells which inserts placeholder 'sleep' for empty
  // 0-5/22-23 hours. Returns null if no real data in any track at this hour.
  function realCatAt(date, hour) {
    const day = data[date];
    if (!day || !day.tracks) return null;
    for (let t = 0; t <= 2; t++) {
      for (let i = 0; i < SLOTS.length; i++) {
        if (SLOTS[i].h !== hour) continue;
        if (day.tracks[t][i]) return day.tracks[t][i];
      }
    }
    return null;
  }

  // Find the contiguous same-cat block around (date, hour), walking across day boundaries
  // on REAL data only (placeholder 'sleep' 不算块的延伸边界).
  // 任何"没有真实数据"的 cell 点击都进入"新建事件"模式，默认 track 0。
  // 该 slot 任一轨道是否含有指定分类
  function slotHasCat(date, i, cat) {
    const day = data[date];
    if (!day || !day.tracks) return false;
    for (let t = 0; t <= 2; t++) if (day.tracks[t][i] === cat) return true;
    return false;
  }

  function findBlock(date, hour, seedCat) {
    const cat = seedCat || realCatAt(date, hour);
    if (!cat) {
      // Empty cell (no real data) → new event mode, regardless of whether day has other data
      return {
        startDate: date, startHour: hour,
        endDate: date, endHour: hour,
        startMin: hour * 60, endMin: (hour + 1) * 60,
        cat: null, track: 0, isPlaceholder: true,
      };
    }
    // N43：slot 空间走块（半小时精度）。锚点=点击小时里第一个含 cat 的 slot
    let aIdx = -1;
    for (let i = 0; i < SLOTS.length; i++) {
      if (SLOTS[i].h !== hour) continue;
      if (slotHasCat(date, i, cat)) { aIdx = i; break; }
    }
    if (aIdx < 0) {
      return {
        startDate: date, startHour: hour, endDate: date, endHour: hour,
        startMin: hour * 60, endMin: (hour + 1) * 60, cat: null, track: 0, isPlaceholder: true,
      };
    }
    let sD = date, sI = aIdx, cap = 5000;
    while (cap-- > 0) {
      const nI = sI === 0 ? SLOTS.length - 1 : sI - 1;
      const nD = sI === 0 ? prevDateKey(sD) : sD;
      if (!slotHasCat(nD, nI, cat)) break;
      sD = nD; sI = nI;
    }
    let eD = date, eI = aIdx;
    cap = 5000;
    while (cap-- > 0) {
      const nI = eI === SLOTS.length - 1 ? 0 : eI + 1;
      const nD = eI === SLOTS.length - 1 ? nextDateKey(eD) : eD;
      if (!slotHasCat(nD, nI, cat)) break;
      eD = nD; eI = nI;
    }
    const track = findTrackForCat(date, hour, cat);
    return {
      startDate: sD, endDate: eD,
      startMin: SLOT_STARTS[sI], endMin: SLOT_ENDS[eI],
      startHour: SLOTS[sI].h, endHour: SLOTS[eI].h,  // 旧整点包络字段（格子高亮等沿用）
      cat, track, isPlaceholder: false,
    };
  }

  // Click outside the panel → close
  React.useEffect(() => {
    if (!openBlock) return;
    function onDocClick(e) {
      if (e.target.closest('.mv-detail-panel')) return;
      if (e.target.closest('.mv-dot')) return;
      setOpenBlock(null);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openBlock]);

  // 移动端打开编辑面板时：把正在编辑那天的行滚到「小时轴正下方」，
  // 便于边编辑时段、边看那天的彩色时间块（offset 动态取 app-header + 小时表头高度，不写死）
  React.useEffect(() => {
    if (!openBlock || typeof window === 'undefined' || window.innerWidth > 720) return;
    const date = openBlock.startDate;
    const raf = requestAnimationFrame(() => {
      const row = document.querySelector(`.mv-grid [data-mv-date="${date}"]`);
      if (!row) return;
      const header = document.querySelector('.app-header');
      const stickyHead = document.querySelector('.mv-sticky-head');
      const offset = (header ? header.offsetHeight : 0) + (stickyHead ? stickyHead.offsetHeight : 0) + 4;
      const rowTop = row.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: Math.max(0, rowTop - offset), behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [openBlock && openBlock.startDate]);

  function catColor(cat) {
    if (!cat) return 'var(--mv-empty)';
    // 直接读 CAT_BY_ID 自动覆盖所有内置/自定义分类
    const c = CAT_BY_ID[cat];
    return (c && c.color) || 'var(--mv-empty)';
  }

  // Perceptual remap: card-bg overlay on card-bg is visually indistinguishable
  // for alpha 0.5-1.0 (same color × same color = same color). To make the slider
  // FEEL even across 0-100, square the value so the upper half of the slider covers
  // alpha 0.25-1.0 (the band where overlay-vs-data contrast is actually visible).
  const visualAlpha = (frostAlpha / 100) ** 2;
  return (
    <div className="monthly-view">
      <div className="mv-sticky-head" style={{ '--frost-alpha': visualAlpha }}>
      <div className="mv-header">
        <div className="mv-header-text">
          <div className="mv-title">
            <button
              type="button"
              className="mv-title-ym"
              onClick={() => { if (window.innerWidth <= 720) setShowYMPicker(true); }}
              title="切换年月"
            >{year} 年 {month} 月<span className="mv-title-caret" aria-hidden="true">▾</span></button>
            <span className="mv-title-rest">的时间块</span>
          </div>
          <div className="mv-sub">{SUB_TEXT}</div>
        </div>
        <div className="mv-frost-control">
          <button
            type="button"
            className="mv-frost-btn"
            onClick={() => setFrostOpen(o => !o)}
            title={`磨砂透明度：${frostAlpha}（点击展开/收起）`}
            aria-label="调节磨砂透明度"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="2" y="3" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.2" fill="none" />
              <line x1="4.5" y1="6.5" x2="11.5" y2="6.5" stroke="currentColor" strokeWidth="1" opacity="0.7" />
              <line x1="4.5" y1="9" x2="11.5" y2="9" stroke="currentColor" strokeWidth="1" opacity="0.45" />
              <line x1="4.5" y1="11" x2="11.5" y2="11" stroke="currentColor" strokeWidth="1" opacity="0.25" />
            </svg>
          </button>
          {frostOpen && (
            <>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={frostAlpha}
                onChange={(e) => setFrostAlpha(+e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                aria-label="磨砂透明度"
              />
              <span className="mv-frost-value">{frostAlpha}</span>
            </>
          )}
        </div>
      </div>

        {/* segment header */}
        <div className="mv-row mv-row-seg">
          <div className="mv-day-num mv-day-head" />
          {MOBILE_SEGMENTS.map(seg => {
            const count = seg.range[1] - seg.range[0] + 1;
            return (
              <div key={seg.label} className={`mv-seg-block tone-${seg.tone} mv-seg-block-name`} style={{ flex: count }}>
                <span className={`mv-seg-name tone-${seg.tone}`}>{seg.label}</span>
              </div>
            );
          })}
        </div>
        {/* hour numbers */}
        <div className="mv-row mv-row-hours">
          <div className="mv-day-num mv-day-head" />
          {MOBILE_SEGMENTS.map(seg => {
            const count = seg.range[1] - seg.range[0] + 1;
            return (
              <div key={seg.label} className={`mv-seg-block tone-${seg.tone}`} style={{ flex: count }}>
                {Array.from({ length: count }).map((_, k) => (
                  <div key={k} className="mv-hour-num">{seg.range[0] + k}</div>
                ))}
              </div>
            );
          })}
        </div>
      </div>{/* /.mv-sticky-head */}
      <div className="mv-grid">
        {/* daily rows */}
        {Array.from({ length: daysInMonth }).map((_, di) => {
          const dnum = di + 1;
          const key = `${year}-${String(month).padStart(2, '0')}-${String(dnum).padStart(2, '0')}`;
          const { real: realCells, display: cells, halves: halfCells } = dayCells(key);
          const hasData = !!data[key];
          const weekday = new Date(year, month - 1, dnum).getDay();
          const isWeekend = weekday === 0 || weekday === 6;
          return (
            <div key={dnum} data-mv-date={key} className={`mv-row ${hasData ? 'has-data' : 'empty'} ${isWeekend ? 'weekend' : ''}`}>
              <div className="mv-day-num">{dnum}</div>
              {MOBILE_SEGMENTS.map(seg => {
                const count = seg.range[1] - seg.range[0] + 1;
                return (
                  <div key={seg.label} className={`mv-seg-block tone-${seg.tone}`} style={{ flex: count }}>
                    {Array.from({ length: count }).map((_, k) => {
                      const h = seg.range[0] + k;
                      const cat = cells[h];                // display 用：真分类 OR 0-5/22-23 的占位 sleep
                      const realCat = realCells[h];        // truth 用：null = 该小时没真数据
                      const half = halfCells[h];           // N40：[前半cat, 后半cat]，前后半不同才非 null
                      // 3 states: real data / placeholder (无真数据但夜间占位 sleep) / empty
                      const isReal = !!realCat;
                      const isPlaceholder = !realCat && !!cat;
                      const isOpen = openBlock &&
                        openBlock.startDate <= key && openBlock.endDate >= key &&
                        ((key !== openBlock.startDate || h >= openBlock.startHour) &&
                         (key !== openBlock.endDate || h <= openBlock.endHour));
                      // 对角线双色（拍板 2026-06-07：135deg 左上=前半、右下=后半；空的半边用空格底色）
                      const bg = half
                        ? `linear-gradient(135deg, ${catColor(half[0])} 50%, ${catColor(half[1])} 50%)`
                        : catColor(cat);
                      const halfName = (c) => c ? ((CAT_BY_ID[c] && (CAT_BY_ID[c].label || CAT_BY_ID[c].name)) || c) : '空';
                      return (
                        <div
                          key={k}
                          className={`mv-dot ${isReal ? 'on' : ''} ${isPlaceholder ? 'is-placeholder' : ''} ${isOpen ? 'is-open' : ''}`}
                          style={isReal ? { background: bg } : undefined}
                          title={half
                            ? `${dnum}日 ${h}:00 ${halfName(half[0])} / ${h}:30 ${halfName(half[1])}`
                            : `${dnum}日 ${h}:00 ${cat || '空'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            // N42：双色格（两半都有分类）→ 面板加 tab，默认加载前半的块（第一个 tab 选中）
                            if (half && half[0] && half[1]) {
                              setOpenBlock(buildSplit(key, h, half).blocks[half[0]]);
                            } else {
                              setOpenBlock(findBlock(key, h));
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="mv-legend">
        {orderedCats.map(c => (
          <div key={c.id} className="mv-legend-item">
            <span className="mv-legend-dot" style={{ background: c.color }} />
            <span>{c.label}</span>
          </div>
        ))}
      </div>
      {/* 移动端：说明文字移到图例下面（PC 隐藏，PC 在标题下显示） */}
      <div className="mv-sub mv-sub-bottom">{SUB_TEXT}</div>
      {showYMPicker && (
        <div className="modal-backdrop" onClick={() => setShowYMPicker(false)}>
          <div className="ym-picker-pop" onClick={e => e.stopPropagation()}>
            <div className="ym-picker-head">
              <span>选择年月</span>
              <button type="button" className="ym-picker-close" onClick={() => setShowYMPicker(false)}>✕</button>
            </div>
            <div className="ym-years">
              {ymYears.map(y => (
                <button
                  key={y}
                  type="button"
                  className={`ym-year ${y === year ? 'sel' : ''}`}
                  onClick={() => { if (y !== year && onChange) onChange(y, ymMonthsFor(y)[0] || 1); }}
                >{y}</button>
              ))}
            </div>
            <div className="ym-months">
              {ymMonthsFor(year).map(m => (
                <button
                  key={m}
                  type="button"
                  className={`ym-month ${m === month ? 'sel' : ''}`}
                  onClick={() => { onChange && onChange(year, m); setShowYMPicker(false); }}
                >{m}月</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Read note for a (date, hour, track): note is on the first slot of the hour
function getNoteAt(data, date, startMin, track) {
  const day = data[date];
  if (!day || !day.notes) return '';
  // N43：精确起点 slot 的备注优先；旧整点起点（startMin=整点）时顺扫同小时其余 slot 兜底
  const hour = Math.floor(startMin / 60);
  for (let i = 0; i < SLOTS.length; i++) {
    if (SLOTS[i].h !== hour) continue;
    if (SLOT_STARTS[i] < startMin) continue;
    const n = day.notes[`${track}:${i}`];
    if (n) return n;
  }
  return '';
}

function formatRange(block) {
  const s = `${block.startDate} ${String(block.startHour).padStart(2, '0')}:00`;
  // end hour is inclusive last hour → end label = endHour+1 (24 if 23)
  const eh = block.endHour + 1;
  const eDate = eh === 24 ? block.endDate : block.endDate;
  const eHourLabel = eh === 24 ? '24:00' : `${String(eh).padStart(2, '0')}:00`;
  const sameDay = block.startDate === block.endDate;
  if (sameDay) return `${block.startDate} · ${String(block.startHour).padStart(2, '0')}:00 – ${eHourLabel}`;
  return `${block.startDate} ${String(block.startHour).padStart(2, '0')}:00 → ${block.endDate} ${eHourLabel}`;
}

function hourSpan(block) {
  // Compute total hours across days
  let h = 0;
  let d = block.startDate;
  let hr = block.startHour;
  let cap = 200;
  while (cap-- > 0) {
    h++;
    if (d === block.endDate && hr === block.endHour) break;
    if (hr === 23) {
      hr = 0;
      const dObj = parseDate(d); dObj.setDate(dObj.getDate() + 1); d = dateKey(dObj);
    } else hr++;
  }
  return h;
}

const TRACK_LABELS = ['娱乐·吃', '睡眠', '运动'];
// 跟日表 `dt-meta-track` 同一套家族色：吃·娱乐→黄、睡眠→紫、运动→玫红
const TRACK_FAMILY_COLORS = ['var(--c-fun)', 'var(--c-sleep)', 'var(--c-exer)'];

function MonthDetailPanel({ block, data, onClose, onSave, onSwitchBlock, quickApplyIds, onQuickApplyChange, defaultQuickApplyIds, customCats, catOrder }) {
  const initialNote = getNoteAt(data, block.startDate, block.startMin ?? block.startHour * 60, block.track);
  const [cat, setCat] = React.useState(block.cat || '');
  const [track, setTrack] = React.useState(block.track ?? 0);
  const [expandedPomos, setExpandedPomos] = React.useState(() => new Set());
  const [showQuickApplyEditor, setShowQuickApplyEditor] = React.useState(false);
  function togglePomoExpand(id) {
    setExpandedPomos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  const [note, setNote] = React.useState(initialNote || '');
  const [startDate, setStartDate] = React.useState(block.startDate);
  const [startMin, setStartMin] = React.useState(block.startMin ?? block.startHour * 60);
  const [endDate, setEndDate] = React.useState(block.endDate);
  const [endMin, setEndMin] = React.useState(block.endMin ?? (block.endHour + 1) * 60);
  const [collapsed, setCollapsed] = React.useState(false);
  // Reset when switching blocks
  React.useEffect(() => {
    setCat(block.cat || '');
    setTrack(block.track ?? 0);
    setNote(initialNote || '');
    setStartDate(block.startDate);
    setStartMin(block.startMin ?? block.startHour * 60);
    setEndDate(block.endDate);
    setEndMin(block.endMin ?? (block.endHour + 1) * 60);
  }, [block.startDate, block.startMin, block.startHour, block.endDate, block.endMin, block.endHour, block.isPlaceholder, initialNote]);

  // Validate: start < end（N43：分钟粒度，end 为开区间）
  const rangeValid = (startDate < endDate) || (startDate === endDate && startMin < endMin);
  const canSave = !!cat && rangeValid;
  // 跨天时长 = 日期差(分钟) + 当日分钟差；0.5 小时步进
  const editedSpan = rangeValid
    ? Math.round((((parseDate(endDate) - parseDate(startDate)) / 60000) + (endMin - startMin)) / 60 * 10) / 10
    : 0;

  // Detect overwrite conflicts: slots in new range NOT in original range that already have data on chosen track
  // N43：slot 粒度（半小时格不再误报整点）
  function* walkSlots(rg) {
    let d = rg.startDate;
    let i = SLOT_STARTS.indexOf(rg.startMin);
    if (i < 0) return;
    let cap = 5000;
    while (cap-- > 0) {
      yield [d, i];
      if (d === rg.endDate && SLOT_ENDS[i] === rg.endMin) return;
      if (i === SLOTS.length - 1) { i = 0; d = nextDateKey(d); } else i++;
    }
  }
  function findConflicts() {
    if (!rangeValid) return [];
    const origSet = new Set();
    if (!block.isPlaceholder) {
      const orig = {
        startDate: block.startDate, endDate: block.endDate,
        startMin: block.startMin ?? block.startHour * 60,
        endMin: block.endMin ?? (block.endHour + 1) * 60,
      };
      for (const [d, i] of walkSlots(orig)) origSet.add(`${d}:${i}`);
    }
    const conflicts = [];
    for (const [d, i] of walkSlots({ startDate, endDate, startMin, endMin })) {
      if (origSet.has(`${d}:${i}`)) continue;
      const day = data[d];
      if (!day || !day.tracks) continue;
      const c = day.tracks[track][i];
      if (c && c !== cat) conflicts.push({ d, i, existing: c });
    }
    return conflicts;
  }

  function handleSave() {
    const conflicts = findConflicts();
    if (conflicts.length > 0) {
      const sample = conflicts.slice(0, 3).map(c => {
        const cInfo = CAT_BY_ID[c.existing];
        return `${c.d} ${fmtMin(SLOT_STARTS[c.i])}（${(cInfo && (cInfo.label || cInfo.name)) || c.existing}）`;
      }).join('、');
      const more = conflicts.length > 3 ? `…等共 ${conflicts.length} 处` : '';
      if (!window.confirm(`新时段覆盖了已有的「${TRACK_LABELS[track]}」分类数据：\n${sample}${more}\n\n确认覆盖？`)) return;
    }
    onSave({
      startDate, endDate, startMin, endMin, cat, track, note,
      // 旧整点字段（兼容）：endHour 含尾小时
      startHour: Math.floor(startMin / 60), endHour: Math.floor((endMin - 1) / 60),
    });
  }

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className={`mv-detail-panel ${collapsed ? 'collapsed' : ''}`} onMouseDown={(e) => e.stopPropagation()}>
      <div className="mv-detail-head">
        <button
          className="mv-detail-collapse"
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? '展开' : '折叠'}
        >{collapsed ? '‹' : '›'}</button>
        {!collapsed && <div className="mv-detail-title">{block.isPlaceholder ? '新建事件' : '编辑时段'}</div>}
        {!collapsed && <button className="mv-detail-close" onClick={onClose} title="关闭">×</button>}
      </div>
      {!collapsed && <div className="mv-detail-body">
        {/* N42：双色格（前后半各有不同分类）→ 顶部分类 tab，切换面板加载哪个分类的块。
            只有这一行是新增的，面板其余（时段/轨道/分类/备注/保存）原样不动（用户拍板 2026-06-07） */}
        {block.split && block.split.cats[0] && block.split.cats[1] && (
          <div className="mv-half-tabs">
            {block.split.cats.map((cid) => {
              const ci = CAT_BY_ID[cid];
              const on = cid === block.cat;
              const color = (ci && ci.color) || 'var(--text-faint)';
              return (
                <button
                  key={cid}
                  type="button"
                  className={`mv-half-tab ${on ? 'on' : ''}`}
                  style={on ? { borderTopColor: color, borderLeftColor: color, borderRightColor: color } : undefined}
                  onClick={() => { if (!on && onSwitchBlock) onSwitchBlock(block.split.blocks[cid]); }}
                >{(ci && (ci.label || ci.name)) || cid}</button>
              );
            })}
          </div>
        )}
        <div className="mv-detail-field">
          <label>时段{rangeValid && <span className="mv-detail-span"> · {editedSpan} 小时</span>}</label>
          <div className="mv-detail-time-row">
            <input
              type="date"
              className="mv-detail-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <select
              className="mv-detail-hr"
              value={startMin}
              onChange={(e) => setStartMin(+e.target.value)}
            >
              {SLOT_STARTS.map((m, i) => (
                <option key={i} value={m}>{fmtMin(m)}</option>
              ))}
            </select>
          </div>
          <div className="mv-detail-time-row">
            <span className="mv-detail-time-dash">至</span>
            <input
              type="date"
              className="mv-detail-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <select
              className="mv-detail-hr"
              value={endMin}
              onChange={(e) => setEndMin(+e.target.value)}
            >
              {SLOT_ENDS.map((m, i) => (
                <option key={i} value={m}>{fmtMin(m)}</option>
              ))}
            </select>
          </div>
          {!rangeValid && <div className="mv-detail-warn">⚠ 结束时间不能早于开始时间</div>}
        </div>
        <div className="mv-detail-field">
          <label>轨道</label>
          <div className="mv-detail-tracks">
            {TRACK_LABELS.map((l, t) => {
              const fc = TRACK_FAMILY_COLORS[t];
              const isOn = track === t;
              return (
                <button
                  key={t}
                  type="button"
                  className={`mv-detail-track-btn ${isOn ? 'on' : ''}`}
                  style={{
                    color: isOn ? 'var(--swatch-text)' : fc,
                    background: isOn ? fc : 'transparent',
                    borderColor: fc,
                  }}
                  onClick={() => setTrack(t)}
                >{l}</button>
              );
            })}
          </div>
        </div>
        <div className="mv-detail-field">
          {/* 原有分类缀在标签后（同「时段 · n 小时」样式）：改分类时随时能看到这段原来是什么 */}
          <label>分类{!block.isPlaceholder && block.cat && CAT_BY_ID[block.cat] && (
            <span className="mv-detail-span"> · 原「{CAT_BY_ID[block.cat].label || CAT_BY_ID[block.cat].name}」</span>
          )}</label>
          {/* 跟日表"快速应用分类"同款：7 个常用 + [+] 编辑入口 */}
          <div className="mv-detail-cats dr-cats">
            {(quickApplyIds || []).map((id, idx) => {
              const c = CAT_BY_ID[id];
              if (!c) return null;
              const displayKey = c.key || (idx === 7 ? '8' : idx === 8 ? '9' : null);
              const isOn = cat === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`dr-cat ${isOn ? 'on' : ''}`}
                  style={{ background: c.block || isOn ? c.color : 'transparent', color: c.block || isOn ? 'var(--swatch-text)' : c.color, borderColor: c.color }}
                  onClick={() => setCat(c.id)}
                  title={`${c.label}${displayKey ? ' (' + displayKey + ')' : ''}`}
                >
                  {c.short}
                  {displayKey && <span className="dr-key">{displayKey}</span>}
                </button>
              );
            })}
            <button
              type="button"
              className="dr-cat dr-cat-add"
              onClick={() => setShowQuickApplyEditor(true)}
              title="自定义快速应用分类"
            >+</button>
          </div>
        </div>
        {(() => {
          // 番茄记录（只读）：紧贴时间，亲密性原则 —— 比备注更靠近时段编辑区
          if (!data) return null;
          const logs = [];
          let d = startDate, cap = 100;
          while (cap-- > 0) {
            const day = data[d];
            if (day && Array.isArray(day.pomoLogs)) {
              const blockMinStart = (d === startDate) ? startMin : 0;
              const blockMinEnd = (d === endDate) ? endMin : 1440;
              // 用 block 原始轨道（block.track）筛选番茄，不跟 track 状态变 —— 用户切换轨道时番茄记录保留
              const filterTrack = block.track != null ? block.track : track;
              day.pomoLogs.forEach(l => {
                if (!l.applied || l.appliedTrack !== filterTrack) return;
                const ls = new Date(l.startMs), le = new Date(l.endMs);
                const logMinStart = ls.getHours() * 60 + ls.getMinutes() + ls.getSeconds() / 60;
                const logMinEnd = le.getHours() * 60 + le.getMinutes() + le.getSeconds() / 60;
                if (logMinEnd > blockMinStart && logMinStart < blockMinEnd) logs.push(l);
              });
            }
            if (d === endDate) break;
            const dObj = parseDate(d); dObj.setDate(dObj.getDate() + 1); d = dateKey(dObj);
          }
          if (logs.length === 0) return null;
          const fmt = ms => { const dd = new Date(ms); return `${pad(dd.getHours())}:${pad(dd.getMinutes())}:${pad(dd.getSeconds())}`; };
          const fmtDur = m => (m >= 60 && m % 60 === 0) ? `${m/60}h` : `${m}m`;
          const totalMin = (editedSpan || 0) * 60;
          return (
            <div className="mv-detail-field mv-detail-pomo-field">
              <label>番茄记录 <span className="mv-detail-pomo-readonly">（不可编辑）</span></label>
              <div className="mv-detail-pomo-timeline">
                {logs.map(l => {
                  const lc = CAT_BY_ID[l.catId];
                  const dotColor = lc ? lc.color : 'var(--accent)';
                  const pct = totalMin > 0 ? Math.min(100, (l.durationMin / totalMin) * 100) : 100;
                  const pctLabel = pct >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10;
                  // 展示前合并相邻 sleep（旧数据兜底）
                  const mergedPauses = [];
                  (l.pauses || []).forEach(p => {
                    if (p.kind === 'sleep' && mergedPauses.length > 0) {
                      const last = mergedPauses[mergedPauses.length - 1];
                      if (last.kind === 'sleep' && (p.at - last.resumedAt) <= 2000) {
                        mergedPauses[mergedPauses.length - 1] = { ...last, resumedAt: p.resumedAt };
                        return;
                      }
                    }
                    mergedPauses.push(p);
                  });
                  const pauses = mergedPauses;
                  const fmtPauses = ps => ps.map((p, i) => {
                    const sec = Math.max(1, Math.round((p.resumedAt - p.at) / 1000));
                    const kind = p.kind === 'sleep' ? '休眠' : '手动暂停';
                    return `${i + 1}. ${kind} ${fmt(p.at)} → ${fmt(p.resumedAt)}（${sec}s）`;
                  }).join('\n');
                  const events = [];
                  pauses.forEach((p, idx) => {
                    const sec = Math.max(1, Math.round((p.resumedAt - p.at) / 1000));
                    const fmtS = s => s >= 60 ? `${Math.floor(s/60)}m${s%60 ? s%60+'s' : ''}` : `${s}s`;
                    if (p.kind === 'sleep') {
                      events.push({ at: p.at, kind: 'sleep', icon: '💤', label: '系统休眠（计入）', time: `${fmt(p.at)} → ${fmt(p.resumedAt)}`, sec: fmtS(sec), key: `s-${idx}` });
                    } else {
                      events.push({ at: p.at, kind: 'mp', icon: '⏸', label: '暂停', time: fmt(p.at), key: `mp-${idx}` });
                      events.push({ at: p.resumedAt, kind: 'mr', icon: '▶', label: '继续', time: fmt(p.resumedAt), sec: fmtS(sec), key: `mr-${idx}` });
                    }
                  });
                  events.sort((a, b) => a.at - b.at);
                  const isExpanded = expandedPomos.has(l.id);
                  return (
                    <div key={l.id} className={`mv-detail-pomo-node-wrap ${isExpanded ? 'expanded' : ''}`} style={{ '--pomo-dot': dotColor }}>
                      <div className="mv-detail-pomo-node" style={{ '--pomo-pct': pct }}>
                        <span className="mv-detail-pomo-pie" data-pct={`占整段 ${pctLabel}%`} />
                        <span className="mv-detail-pomo-time">{fmt(l.startMs)} - {fmt(l.endMs)}</span>
                        <span className="mv-detail-pomo-tomato" aria-hidden="true">🍅</span>
                        <span className="mv-detail-pomo-dur">{fmtDur(l.durationMin)}</span>
                        {pauses.length > 0 && (
                          <button
                            type="button"
                            className="mv-detail-pomo-pauses"
                            onClick={() => togglePomoExpand(l.id)}
                            title={isExpanded ? '收起暂停明细' : '展开暂停明细'}
                          >
                            ⏸ {pauses.length}
                            <span className="mv-detail-pomo-pauses-arrow" aria-hidden="true">{isExpanded ? '▴' : '▾'}</span>
                          </button>
                        )}
                      </div>
                      {isExpanded && events.length > 0 && (
                        <div className="mv-detail-pomo-events">
                          {events.map(e => (
                            <div key={e.key} className={`mv-detail-pomo-event kind-${e.kind}`}>
                              <span className="mv-detail-pomo-event-icon">{e.icon}</span>
                              <span className="mv-detail-pomo-event-label">{e.label}</span>
                              <span className="mv-detail-pomo-event-time">{e.time}</span>
                              <span className="mv-detail-pomo-event-sec">{e.sec || ''}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
        <div className="mv-detail-field">
          <label>备注</label>
          <textarea
            className="mv-detail-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="一句话描述这段时间…"
            rows={3}
          />
        </div>
        <div className="mv-detail-actions">
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn primary" disabled={!canSave} onClick={handleSave}>保存</button>
        </div>
      </div>}
      {showQuickApplyEditor && (
        <QuickApplyShuttleModal
          current={quickApplyIds || []}
          defaultIds={defaultQuickApplyIds}
          customCats={customCats}
          catOrder={catOrder}
          onSave={(ids) => { onQuickApplyChange && onQuickApplyChange(ids); setShowQuickApplyEditor(false); }}
          onClose={() => setShowQuickApplyEditor(false)}
        />
      )}
    </div>
  );
}

// 从 data 算出「有数据的年 → 月」选项（PC 侧栏 nav 和移动端标题弹窗共用）
function buildYearMonths(data, year, month) {
  const yearMonths = {};
  Object.keys(data || {}).forEach(k => {
    const [y, m] = k.split('-').map(Number);
    if (!yearMonths[y]) yearMonths[y] = new Set();
    yearMonths[y].add(m);
  });
  // Always include selected year + previous year as the fallback span
  const baseYear = Math.max(year, ...Object.keys(yearMonths).map(Number).concat([year]));
  const years = [];
  for (let y = baseYear; y >= baseYear - 1; y--) years.push(y);
  Object.keys(yearMonths).map(Number).forEach(y => { if (!years.includes(y)) years.push(y); });
  years.sort((a, b) => b - a);
  const monthsFor = (y) => {
    const ms = yearMonths[y] ? [...yearMonths[y]] : [];
    if (y === year && !ms.includes(month)) ms.push(month);
    // 升序（1→12）：1 月在左、12 月在右
    return ms.sort((a, b) => a - b);
  };
  return { years, monthsFor };
}

// Vertical month/year navigation rail for the monthly view.
function MonthYearNav({ data, year, month, onChange }) {
  const { years, monthsFor } = buildYearMonths(data, year, month);

  return (
    <nav className="mv-nav" aria-label="月份年份切换">
      {years.map(y => {
        const isSel = y === year;
        const months = monthsFor(y);
        return (
          <div key={y} className={`mv-nav-year-group ${isSel ? 'sel' : ''}`}>
            <button
              className={`mv-nav-year ${isSel ? 'sel' : ''}`}
              onClick={() => {
                if (y === year) return;
                const fallbackMonth = months[0] || 1;
                onChange(y, fallbackMonth);
              }}
            >
              {y}
            </button>
            {isSel && months.length > 0 && (
              <div className="mv-nav-months">
                {months.map(m => (
                  <button
                    key={m}
                    className={`mv-nav-month ${m === month ? 'sel' : ''}`}
                    onClick={() => onChange(y, m)}
                  >
                    <span className="mv-nav-dot" aria-hidden="true">•</span>
                    <span className="mv-nav-month-text">{m}月</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export { MonthlyView, MonthYearNav, MonthDetailPanel };
